import React, { useImperativeHandle, useState, useRef, useEffect } from 'react';
import { useMessageScroll } from '../hooks/useMessageScroll';
import { useTranslation } from '../localization/useAppTranslation';

export interface MessageItem {
  id: string | number;
  [key: string]: any;
}

export interface CustomMessageListProps {
  messages: MessageItem[];
  children: React.ReactNode;
  className?: string;
  autoScrollToBottom?: boolean;
  autoScrollToBottomOnMount?: boolean;
}

/**
 * Custom message list component with scroll-to-message functionality.
 * Provides ref methods for programmatic scrolling and auto-scroll behavior.
 */
export const CustomMessageList = React.forwardRef<
  CustomMessageListHandle,
  CustomMessageListProps
>(
  (
    {
      messages,
      children,
      className = '',
      autoScrollToBottom = true,
      autoScrollToBottomOnMount = true,
    },
    ref
  ) => {
    const { t } = useTranslation('chat');
    const [isScrolling, setIsScrolling] = useState(false);
    const hideScrollbarTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const isResizingRef = useRef(false);

    const { containerRef, scrollToMessage, scrollToBottom } = useMessageScroll(
      messages,
      autoScrollToBottomOnMount
    );

    // Unified function to show scrollbar and schedule hiding
    const showScrollbar = () => {
      setIsScrolling(true);

      // Clear any existing timeout
      if (hideScrollbarTimeoutRef.current) {
        clearTimeout(hideScrollbarTimeoutRef.current);
      }

      // Schedule hiding after 1.5 seconds of inactivity
      hideScrollbarTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1500);
    };

    // Handle scroll events
    const handleScroll = () => {
      showScrollbar();
    };

    // Handle mouse move events - show scrollbar and bounce the timer
    const handleMouseMove = () => {
      showScrollbar();
    };

    // Handle mouse enter to immediately show scrollbar
    const handleMouseEnter = () => {
      setIsScrolling(true);
      // Clear existing timeout so it doesn't hide while hovering
      if (hideScrollbarTimeoutRef.current) {
        clearTimeout(hideScrollbarTimeoutRef.current);
      }
    };

    // Handle mouse leave - start the timeout to hide scrollbar
    const handleMouseLeave = () => {
      if (hideScrollbarTimeoutRef.current) {
        clearTimeout(hideScrollbarTimeoutRef.current);
      }
      hideScrollbarTimeoutRef.current = setTimeout(() => {
        setIsScrolling(false);
      }, 1500);
    };

    // Observe container height changes (typewriter effect, new messages)
    useEffect(() => {
      if (!containerRef.current) return;

      resizeObserverRef.current = new ResizeObserver(() => {
        showScrollbar();
      });

      resizeObserverRef.current.observe(containerRef.current);

      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }, []);

    // Initial show when messages change
    useEffect(() => {
      showScrollbar();
    }, [messages]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (hideScrollbarTimeoutRef.current) {
          clearTimeout(hideScrollbarTimeoutRef.current);
        }
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
        }
      };
    }, []);

    // Expose methods via ref
    useImperativeHandle(ref, () => ({
      scrollToMessage,
      scrollToBottom,
      container: containerRef.current,
    }));

    const messageListClass = [
      'custom-message-list',
      isScrolling && 'scrolling',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        ref={containerRef}
        className={messageListClass}
        onScroll={handleScroll}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="log"
        aria-live="polite"
        aria-label={t('messageList.ariaLabel')}
        style={{
          // Crucial: Extra padding allows the last message to reach the top
          // Height of viewport minus desired offset
          paddingBottom: 'calc(100vh - 200px)'
        }}
      >
        {children}
      </div>
    );
  }
);

CustomMessageList.displayName = 'CustomMessageList';

export interface CustomMessageListHandle {
  /**
   * Scroll to a specific message by ID
   * @param messageId The ID of the message to scroll to
   * @returns true if message was found and scrolled, false otherwise
   */
  scrollToMessage: (
    messageId: string | number
  ) => boolean;

  /**
   * Scroll to the bottom of the message list
   * @param options ScrollIntoView options
   * @returns true if scroll was performed
   */
  scrollToBottom: (options?: ScrollIntoViewOptions) => boolean;

  /**
   * Direct reference to the container DOM element
   */
  container: HTMLDivElement | null;
}
